import { faClockRotateLeft, faEdit, faEllipsisVertical, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import {
  ConfirmDialog,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { ListItem } from 'src/components/ListItem';
import { SearchParamsPagination } from 'src/components/Pagination';
import { Scroller } from 'src/components/Scroller';
import { SearchParamSearchTextField } from 'src/components/SearchTextField';
import { SectionToolbar } from 'src/components/SectionToolbar';
import { DataTable } from 'src/components/Table/DataTable';
import { Theme } from 'src/components/muiTheme/types';
import { useMediaQuery } from 'src/components/muiTheme/utils';
import { FranchiseFee } from 'src/models/hq/franchise-fee/franchise-fee';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { ContainerBreakout } from 'src/shared/AppFrame/ContainerBreakout';
import {
  BooleanFilter,
  DateRangeFilter,
  Filters,
  SelectFilter,
} from 'src/shared/Filters/Filters';
import { useHQPermissionCheck } from 'src/shared/HQ/useHQPermissionCheck';
import { VersionHistory } from 'src/shared/VersionHistory';
import { formatFullDate } from 'src/utils/dateFormatter';
import { formatCurrency } from 'src/utils/numberFormatter';
import toast from 'src/utils/toast';

import { useFranchiseFeePage } from './useFranchiseFeePage';


export default function FranchiseFeesPage() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'franchiseFees.page',
  });
  const navigate = useNavigate();
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [confirmDialogProps, confirm] = useConfirmDialogComponent();
  const { canEditHQFranchiseFees } = useHQPermissionCheck();

  // Version history state
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [selectedFee, setSelectedFee] = useState<FranchiseFee | null>(null);

  // URL search params for sorting

  const {
    fees,
    feesIsLoading,
    feesIsError,
    deleteFranchiseFee,
    params,
    updateParam,
    updateFilterParam,
    getFilterParamValue,
    resetFilterParams,
  } = useFranchiseFeePage(undefined);

  const handleDelete = async (row: FranchiseFee) => {
    const confirmed = await confirm({
      title: t('confirmDeleteTitle'),
      message: t('confirmDeleteMessage', { name: row.name }),
    });

    if (confirmed) {
      try {
        await deleteFranchiseFee.mutateAsync(String(row.id));
        toast.success(t('deleteSuccess'));
      } catch (error) {
        toast.error(t('deleteError'));
      }
    }
  };

  const handleViewVersionHistory = (row: FranchiseFee) => {
    setSelectedFee(row);
    setVersionHistoryOpen(true);
  };

  const handleCloseVersionHistory = () => {
    setVersionHistoryOpen(false);
    setSelectedFee(null);
  };

  const handleSortBy = (columnId: string | ((prev: string) => string)) => {
    const value =
      typeof columnId === 'function' ? columnId(params.sortBy) : columnId;
    updateParam('sortBy', value);
  };

  const handleSortDir = (
    direction: 'asc' | 'desc' | ((prev: 'asc' | 'desc') => 'asc' | 'desc')
  ) => {
    const value =
      typeof direction === 'function' ? direction(params.sortDir) : direction;
    updateParam('sortDir', value);
  };

  const columns = [
    {
      id: 'name',
      label: t('columnName'),
      sortable: true,
    },
    {
      id: 'feeType',
      label: t('columnFeeType'),
      sortable: true,
      format: (value: string) =>
        value === 'flat' ? t('flat') : t('percentage'),
    },
    {
      id: 'feeAmount',
      label: t('columnAmount'),
      sortable: true,
      format: (value: number, row: FranchiseFee) =>
        row.feeType === 'flat'
          ? formatCurrency(row.amount || 0)
          : `${row.percentageAmount}%`,
    },
    {
      id: 'interval',
      label: t('columnInterval'),
      sortable: false,
      format: (value: string, row: FranchiseFee) =>
        row.feeType === 'flat' ? t('monthly') : '--',
    },
    {
      id: 'startDate',
      label: t('columnStartDate'),
      format: (value: string) => formatFullDate(value) || t('inactive'),
      sortable: true,
    },
    {
      id: 'endDate',
      label: t('columnEndDate'),
      format: (value: string) => formatFullDate(value) || '--',
      sortable: true,
    },
  ];

  const filters = [
    {
      field: 'feeType',
      label: t('feeType'),
      Component: SelectFilter,
      props: {
        options: [
          { value: 'flat', label: t('flat') },
          { value: 'percentage', label: t('percentage') },
        ],
      },
    },
    {
      field: 'startDateRange',
      label: t('startDateRange'),
      Component: DateRangeFilter,
    },
    {
      field: 'endDateRange',
      label: t('endDateRange'),
      Component: DateRangeFilter,
    },
    {
      field: 'active',
      label: t('active'),
      Component: BooleanFilter,
    },
  ];

  const appFrameActions = canEditHQFranchiseFees()
    ? [
        {
          label: t('createNewFranchiseFee'),
          icon: faPlus,
          variant: 'contained' as const,
          onClick: () => {
            navigate('/franchise-fees/new');
          },
        },
      ]
    : [];

  return (
    <>
      <AppFrameConfig
        id="franchise-fees"
        containerMaxWidth={'xl'}
        title={t('title')}
        actions={
          <>
            {smDown ? (
              <DropdownMenu
                trigger={
                  <IconButton>
                    <Icon
                      icon={faEllipsisVertical}
                    />
                  </IconButton>
                }
              >
                {appFrameActions.map(({ ...action }) => (
                  <ListItem
                    sx={{ py: 1.5, mb: 1, my: 0 }}
                    key={action.label}
                    primary={action.label}
                    icon={action.icon}
                    onClick={action.onClick}
                    clickable
                    rounded
                  />
                ))}
              </DropdownMenu>
            ) : (
              <>
                {appFrameActions.map(({ ...action }) => (
                  <Button
                    key={action.label}
                    onClick={action.onClick}
                    startIcon={action.icon && <Icon icon={action.icon} />}
                    variant={action.variant}
                  >
                    {action.label}
                  </Button>
                ))}
              </>
            )}
          </>
        }
      />
      <ContainerBreakout>
        <SectionToolbar sx={{ pb: 3, pt: 0, px: 0 }}>
          <SearchParamSearchTextField
            params={params}
            updateParam={updateParam}
            sx={{ width: smDown ? '100%' : '280px' }}
          />
        </SectionToolbar>
        <SectionToolbar sx={{ pb: 3, pt: 0, px: 0 }}>
          <Scroller
            direction="row"
            spacing={2}
            hideScrollbar
            sx={{ mx: -2, px: 2, boxSizing: 'content-box' }}
          >
            <Filters
              filters={filters}
              sortableColumns={columns}
              params={params}
              updateParam={updateParam}
              updateFilterParam={updateFilterParam}
              getFilterParamValue={getFilterParamValue}
              resetFilterParams={resetFilterParams}
            />
          </Scroller>
        </SectionToolbar>
        <Card sx={{ mb: 6 }}>
          <DataTable
            columns={columns}
            rows={fees.franchiseFees}
            noResultsText={
              feesIsError ? t('errorLoadingFees') : t('noFeesFound')
            }
            loading={feesIsLoading}
            totalItems={fees.total}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            setSortBy={handleSortBy}
            setSortDir={handleSortDir}
            rowLinkTo={
              canEditHQFranchiseFees()
                ? (row) => `/franchise-fees/${(row as FranchiseFee).id}/edit`
                : undefined
            }
            rowActions={[
              ...(canEditHQFranchiseFees()
                ? [
                    {
                      label: t('edit'),
                      icon: faEdit,
                      onClick: (row) => {
                        navigate(`/franchise-fees/${row.id}/edit`);
                      },
                    },
                  ]
                : []),
              {
                label: t('versionHistory'),
                icon: faClockRotateLeft,
                onClick: handleViewVersionHistory,
              },
              ...(canEditHQFranchiseFees()
                ? [
                    {
                      label: t('delete'),
                      icon: faTrash,
                      onClick: handleDelete,
                    },
                  ]
                : []),
            ]}
          />
          <SearchParamsPagination
            params={params}
            updateParam={updateParam}
            total={fees.total}
            itemsPerPage={params.perPage}
          />
        </Card>
      </ContainerBreakout>
      <ConfirmDialog {...confirmDialogProps} />
      {selectedFee && (
        <VersionHistory
          open={versionHistoryOpen}
          onClose={handleCloseVersionHistory}
          itemType="Hq::FranchiseFee"
          itemId={selectedFee.id}
        />
      )}
    </>
  );
}
